// Schedule utilities for BuildMyChecks

export const SCHEDULE_FREQUENCIES = [
  { value: 'hourly', label: 'Every Hour', hours: 1 },
  { value: 'daily', label: 'Every Day', hours: 24 },
  { value: 'weekly', label: 'Every Week', hours: 168 },
  { value: 'biweekly', label: 'Every 2 Weeks', hours: 336 },
  { value: 'monthly', label: 'Every Month', days: 30 },
  { value: '2monthly', label: 'Every 2 Months', days: 60 },
  { value: '3monthly', label: 'Every 3 Months', days: 90 },
  { value: '6monthly', label: 'Every 6 Months', days: 180 },
  { value: 'yearly', label: 'Every Year', days: 365 },
  { value: 'custom', label: 'Custom Interval', custom: true }
];

/**
 * Calculate next due date based on frequency
 * @param {string} frequency - The schedule frequency
 * @param {Date|Timestamp} lastCompleted - Last completion date
 * @param {number} customDays - Custom interval in days (if frequency is 'custom')
 * @returns {Date} Next due date
 */
export function calculateNextDueDate(frequency, lastCompleted = new Date(), customDays = null) {
  const date = lastCompleted instanceof Date ? lastCompleted : lastCompleted.toDate();
  const nextDate = new Date(date);

  const freq = SCHEDULE_FREQUENCIES.find(f => f.value === frequency);
  
  if (frequency === 'custom' && customDays) {
    nextDate.setDate(nextDate.getDate() + customDays);
  } else if (freq) {
    if (freq.hours) {
      nextDate.setHours(nextDate.getHours() + freq.hours);
    } else if (freq.days) {
      nextDate.setDate(nextDate.getDate() + freq.days);
    }
  }

  return nextDate;
}

/**
 * Check if a check is within the acceptance window
 * @param {Date} dueDate - The due date
 * @param {number} windowBefore - Days before due date that check can be completed
 * @param {number} windowAfter - Days after due date that check can be completed
 * @param {Date} currentDate - Current date (defaults to now)
 * @returns {boolean} Whether check is within acceptance window
 */
export function isWithinWindow(dueDate, windowBefore = 0, windowAfter = 0, currentDate = new Date()) {
  const due = dueDate instanceof Date ? dueDate : dueDate.toDate();
  const current = currentDate instanceof Date ? currentDate : currentDate.toDate();
  
  // Set times to start of day for date comparison
  const dueStart = new Date(due);
  dueStart.setHours(0, 0, 0, 0);
  
  const currentStart = new Date(current);
  currentStart.setHours(0, 0, 0, 0);
  
  const windowStart = new Date(dueStart);
  windowStart.setDate(windowStart.getDate() - windowBefore);
  
  const windowEnd = new Date(dueStart);
  windowEnd.setDate(windowEnd.getDate() + windowAfter);
  
  return currentStart >= windowStart && currentStart <= windowEnd;
}

/**
 * Get status of a scheduled check
 * @param {Date} dueDate - The due date
 * @param {number} windowBefore - Days before
 * @param {number} windowAfter - Days after
 * @returns {object} Status object with type and label
 */
export function getScheduleStatus(dueDate, windowBefore = 0, windowAfter = 0) {
  const due = dueDate instanceof Date ? dueDate : dueDate.toDate();
  const now = new Date();
  
  // Set to start of day
  const dueStart = new Date(due);
  dueStart.setHours(0, 0, 0, 0);
  
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(todayStart);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const daysDiff = Math.floor((dueStart - todayStart) / (1000 * 60 * 60 * 24));
  
  // Check if overdue (past due date + window)
  const windowEnd = new Date(dueStart);
  windowEnd.setDate(windowEnd.getDate() + windowAfter);
  
  if (todayStart > windowEnd) {
    const daysOverdue = Math.floor((todayStart - windowEnd) / (1000 * 60 * 60 * 24));
    return {
      type: 'overdue',
      label: `Overdue by ${daysOverdue} day${daysOverdue !== 1 ? 's' : ''}`,
      color: '#ef4444',
      priority: 1
    };
  }
  
  // Check if due today
  if (daysDiff === 0) {
    return {
      type: 'due-today',
      label: 'Due Today',
      color: '#f59e0b',
      priority: 2
    };
  }
  
  // Check if due tomorrow
  if (daysDiff === 1) {
    return {
      type: 'due-tomorrow',
      label: 'Due Tomorrow',
      color: '#3b82f6',
      priority: 3
    };
  }
  
  // Check if within acceptance window
  if (isWithinWindow(dueDate, windowBefore, windowAfter)) {
    if (daysDiff < 0) {
      return {
        type: 'available-early',
        label: `Available (${Math.abs(daysDiff)} days early)`,
        color: '#10b981',
        priority: 4
      };
    } else {
      return {
        type: 'upcoming',
        label: `Due in ${daysDiff} days`,
        color: '#6b7280',
        priority: 5
      };
    }
  }
  
  // Upcoming but not yet in window
  return {
    type: 'upcoming',
    label: `Due in ${daysDiff} days`,
    color: '#6b7280',
    priority: 6
  };
}

/**
 * Check if user is assigned to a schedule
 * @param {object} schedule - Schedule object
 * @param {string} userId - User ID to check
 * @param {array} userGroups - User's group IDs
 * @returns {boolean} Whether user is assigned
 */
export function isUserAssigned(schedule, userId, userGroups = []) {
  if (!schedule) return false;
  
  // Check if assigned to all users
  if (schedule.allUsers) return true;
  
  // Check if user is directly assigned
  if (schedule.assignedUsers && schedule.assignedUsers.includes(userId)) {
    return true;
  }
  
  // Check if user is in an assigned group
  if (schedule.assignedGroups && userGroups.some(g => schedule.assignedGroups.includes(g))) {
    return true;
  }
  
  return false;
}

/**
 * Get all scheduled checks for a user
 * @param {array} forms - All forms with schedules
 * @param {string} userId - User ID
 * @param {array} userGroups - User's group IDs
 * @param {boolean} includeCompleted - Include recently completed
 * @returns {array} Scheduled checks
 */
export function getScheduledChecksForUser(forms, userId, userGroups = [], includeCompleted = false) {
  const scheduled = [];
  
  forms.forEach(form => {
    if (!form.schedules || form.schedules.length === 0) return;
    
    form.schedules.forEach(schedule => {
      if (!schedule.enabled) return;
      if (!isUserAssigned(schedule, userId, userGroups)) return;
      
      // Skip if recently completed (unless requested)
      if (!includeCompleted && schedule.lastCompleted) {
        const nextDue = calculateNextDueDate(
          schedule.frequency,
          schedule.lastCompleted,
          schedule.customDays
        );
        const now = new Date();
        if (now < nextDue) return;
      }
      
      const dueDate = schedule.nextDueDate?.toDate() || 
                     calculateNextDueDate(schedule.frequency, schedule.lastCompleted, schedule.customDays);
      
      const status = getScheduleStatus(
        dueDate,
        schedule.windowBefore || 0,
        schedule.windowAfter || 0
      );
      
      // Only include if within window or overdue
      if (isWithinWindow(dueDate, schedule.windowBefore, schedule.windowAfter) || status.type === 'overdue') {
        scheduled.push({
          formId: form.id,
          formTitle: form.title,
          scheduleId: schedule.id,
          dueDate,
          status,
          frequency: schedule.frequency,
          windowBefore: schedule.windowBefore || 0,
          windowAfter: schedule.windowAfter || 0,
          description: schedule.description || '',
          lastCompleted: schedule.lastCompleted
        });
      }
    });
  });
  
  // Sort by priority (overdue first, then due today, etc.)
  scheduled.sort((a, b) => {
    if (a.status.priority !== b.status.priority) {
      return a.status.priority - b.status.priority;
    }
    return a.dueDate - b.dueDate;
  });
  
  return scheduled;
}

/**
 * Format due date for display
 * @param {Date} date - Due date
 * @returns {string} Formatted date string
 */
export function formatDueDate(date) {
  const d = date instanceof Date ? date : date.toDate();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  
  const daysDiff = Math.floor((dueDay - today) / (1000 * 60 * 60 * 24));
  
  if (daysDiff === 0) return 'Today';
  if (daysDiff === 1) return 'Tomorrow';
  if (daysDiff === -1) return 'Yesterday';
  
  const options = { weekday: 'short', month: 'short', day: 'numeric' };
  return d.toLocaleDateString('en-US', options);
}
